import React from 'react';

import {
  Dropdown,
  MultiSelectButton,
  TableCell,
  TableHeaderCell,
} from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  PropertyTypeEnum,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatPropertyType } from '@hcs/utils';

const FIELD = PropertyStateFields.propertyType;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Property Type';
const labelShort = 'Property Type';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyState?.propertyDetails?.propertyType;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatPropertyType(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)}`;
const propertyStatePath: PropertyStatePaths = '/propertyDetails/propertyType';

// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

const convertedPropertyTypeLabel = (propertyTypeLabel: string): string => {
  if (propertyTypeLabel === 'Single Family Detached') return 'Single Family';
  if (propertyTypeLabel === 'Multifamily') return 'Multi-family';
  return propertyTypeLabel;
};

const OPTIONS = [
  PropertyTypeEnum.Sfr,
  PropertyTypeEnum.Condo,
  PropertyTypeEnum.Townhouse,
  PropertyTypeEnum.MultiFamily,
  PropertyTypeEnum.Manufactured,
].map((propertyType) => ({
  value: propertyType,
  label: convertedPropertyTypeLabel(formatPropertyType(propertyType)),
}));

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const value = getValue(propertyStateArgs);

  return (
    <Dropdown
      dataHcName="property-type-edit-dropdown"
      ariaLabel={`Adjust ${label}`}
      className={className}
      onSelect={(value) =>
        onChange({
          path: propertyStatePath,
          value,
          field: FIELD,
        })
      }
      options={OPTIONS}
      value={value}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <MultiSelectButton
      className={className}
      disabled={disabled}
      options={OPTIONS}
      value={spatialFiltersInput.propertyTypeEnum || []}
      onChange={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            propertyTypeEnum: value,
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

export const PROPERTY_TYPE_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
  Filter,
};
